/// <amd-module name="Analytics/Boards/BurndownQueries/WorkItemTypeFieldsQuery" />
import { ODataWorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { FieldFilter, WorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PublicProjectsQueryHelper from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";
import { BurndownQueryBase } from "./BurndownQueryBase";

/**
 * Query work item types with optional list of work item types IDs
 * Note that this requires project IDs for security reasons
 */
export class WorkItemTypeFieldsQuery extends BurndownQueryBase<WorkItemFieldDescriptor[]> {
    private static QuotableTypes = [
        "DateTime",
        "PlainText", // Strings over 255
        "String", // Strings under 256
        "Html",
        "TreePath"
    ];

    public static AnalyticsQueryPathDict: { [key: string]: string } = {
        "System.AreaPath": "Area/AreaPath",
        "System.IterationPath": "Iteration/IterationPath"
    };

    private static generateQueryOptions(pageContext: IVssPageContext, projectIDs: string[], workItemTypes?: string[]): ODataQueryOptions {
        const projects = uniqueSort(projectIDs);
        const projectsClause = QueryUtilities.isInArray("ProjectSK", projects, false /* useQuotes */);
        let $apply = `filter((${projectsClause})`;

        // Adding optional filter for work item types
        // This is used by the widget when getting metadata for a known list of types
        // The configuration will not pass this parameter, getting all work item types for filter setup
        if (!!workItemTypes && workItemTypes.length > 0) {
            $apply += ` and (${QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */)})`;
        }
        $apply += ")/groupby((FieldName,FieldType,FieldReferenceName))";

        return PublicProjectsQueryHelper.forceProjectScoping(pageContext, projects, {
            entityType: "WorkItemTypeFields",
            oDataVersion: latestODataVersion,
            $apply: $apply,
            $orderby: "FieldName"
        });
    }

    public static defaultFieldExtractor = (fieldFilter: FieldFilter) => {
        return fieldFilter.queryValue;
    };
    constructor(pageContext: IVssPageContext, projectIDs: string[], workItemTypes?: string[], commandName?: string) {
        super(WorkItemTypeFieldsQuery.generateQueryOptions(pageContext, projectIDs, workItemTypes), commandName);
    }

    public getQueryName(): string {
        return "WorkItemTypeFieldsQuery";
    }

    protected interpretQueryResults(sourceFieldDescriptors: { value: ODataWorkItemFieldDescriptor[] }): WorkItemFieldDescriptor[] {
        // There are several data enrichment steps to perform here.
        const descriptors: WorkItemFieldDescriptor[] = [];

        // Figure out the corresponding property path in analytics for this field descriptor
        sourceFieldDescriptors.value.map(value => {
            descriptors.push(this.translateDescriptor(value));
        });

        return descriptors;
    }

    private translateDescriptor(sourceDescriptor: ODataWorkItemFieldDescriptor): WorkItemFieldDescriptor {
        // Check if the type is one of types that needs to be wrapped in quotes and escaped when passed to odata
        const useQuotes = WorkItemTypeFieldsQuery.QuotableTypes.indexOf(sourceDescriptor.FieldType) >= 0;

        let analyticsFilterName = WitFieldUtilities.getFieldODataPropertyName(sourceDescriptor.FieldReferenceName);

        // For certain well-known fields, analytics represents them as navigation properties,
        // so we need to construct the filter a little differently.
        if (WorkItemTypeFieldsQuery.AnalyticsQueryPathDict.hasOwnProperty(sourceDescriptor.FieldReferenceName)) {
            analyticsFilterName = WorkItemTypeFieldsQuery.AnalyticsQueryPathDict[sourceDescriptor.FieldReferenceName];
        }

        // Figure out the corresponding way to retrieve the query value
        const getAnalyticsQueryValueFunc: (fieldFilter: FieldFilter) => string = WorkItemTypeFieldsQuery.defaultFieldExtractor;

        return {
            FieldName: sourceDescriptor.FieldName,
            FieldReferenceName: sourceDescriptor.FieldReferenceName,
            FieldType: sourceDescriptor.FieldType,
            UseQuotes: useQuotes,
            AnalyticsFilterName: analyticsFilterName,
            GetAnalyticsQueryValueFunc: getAnalyticsQueryValueFunc
        };
    }
}
